/**
 * AnalyticsUpgradePrompt Component
 *
 * Displays upgrade prompt when analytics features are not available for current plan.
 * Shows feature comparison and upgrade CTA.
 *
 * Usage:
 *   <AnalyticsUpgradePrompt
 *     level="none"
 *     feature="AI Insights"
 *     requiredLevel="full"
 *     onUpgradeClick={() => navigate('/settings/subscription')}
 *   />
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED !== 'false';

export interface AnalyticsUpgradePromptProps {
  level: 'none' | 'advanced' | 'full';
  feature?: string;
  requiredLevel?: 'advanced' | 'full';
  compact?: boolean;
  onUpgradeClick?: () => void;
}

const featureDescriptions: Record<string, { feature: string; tier: string; benefit: string }> = {
  insights: {
    feature: 'AI-Powered Insights',
    tier: 'Enterprise',
    benefit: 'Get intelligent recommendations and automatic anomaly detection',
  },
  breakdowns: {
    feature: 'Advanced Breakdowns',
    tier: 'Enterprise',
    benefit: 'Analyze performance by demographics, device, location, and more',
  },
  charts: {
    feature: 'Performance Charts',
    tier: 'Pro',
    benefit: 'Visualize trends and patterns in your campaign data',
  },
  export: {
    feature: 'Data Export',
    tier: 'Pro',
    benefit: 'Export metrics to CSV, PDF, and JSON formats',
  },
  history: {
    feature: 'Historical Data',
    tier: 'Pro',
    benefit: 'Access 90 days of historical analytics (or 365 days for Enterprise)',
  },
};

export const AnalyticsUpgradePrompt: React.FC<AnalyticsUpgradePromptProps> = ({
  level,
  feature = 'analytics',
  requiredLevel = 'advanced',
  compact = false,
  onUpgradeClick,
}) => {
  if (level === 'full' || !paymentsEnabled) {
    return null; // No prompt needed for Enterprise or when payments are disabled
  }

  const isNone = level === 'none';
  const needsEnterprise = requiredLevel === 'full';

  const title = isNone
    ? 'Analytics Features Locked'
    : needsEnterprise
      ? 'Enterprise Feature'
      : 'Pro Feature';

  const description = isNone
    ? 'Upgrade to Pro to access detailed campaign analytics, charts, and performance metrics.'
    : needsEnterprise
      ? 'This feature is available exclusively with Enterprise plan. AI-powered insights, advanced breakdowns, and more.'
      : 'Unlock this feature with Pro plan. Get charts, exports, and 90 days of historical data.';

  const ctaText = isNone
    ? 'Upgrade to Pro'
    : needsEnterprise
      ? 'Upgrade to Enterprise'
      : 'Upgrade to Pro';

  if (compact) {
    return (
      <div className="flex items-center justify-between p-4 bg-warning-soft border border-warning/25 rounded-lg">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-warning flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-ink">{title}</p>
            <p className="text-xs text-ink-2">{description}</p>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpgradeClick}
        >
          {ctaText}
        </Button>
      </div>
    );
  }

  return (
    <Card variant="outlined" padding="lg" className="border-warning/25 bg-warning-soft">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-warning/15">
              <svg className="w-6 h-6 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">{title}</h3>
              <p className="text-sm text-ink-2 mt-1">{description}</p>
            </div>
          </div>

          {/* Feature comparison hint */}
          <div className="mt-4 pt-4 border-t border-warning/25">
            <p className="text-xs text-warning font-medium mb-2">What you'll get:</p>
            <ul className="space-y-2">
              {isNone ? (
                <>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Performance charts and trends
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    90 days of data history
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Export to CSV and PDF
                  </li>
                </>
              ) : needsEnterprise ? (
                <>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    AI-powered insights and recommendations
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Advanced demographic breakdowns
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    1 year of historical data
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Advanced demographic breakdowns
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    JSON export format
                  </li>
                  <li className="flex items-center gap-2 text-sm text-ink-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Unlimited data history
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onUpgradeClick}
          >
            {ctaText}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AnalyticsUpgradePrompt;
